import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  SearchProposalClient,
  GetProposalClient,
  DeleteProposalClient,
  UpdateProposalClient,
  UpdateProposalStatusClient,
  CreateProposalClient,
  ArchivingProposalClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const searchProposalClient = new SearchProposalClient(SERVICE_URL)
const getProposalClient = new GetProposalClient(SERVICE_URL)
const createProposalClient = new CreateProposalClient(SERVICE_URL)
const deleteProposalClient = new DeleteProposalClient(SERVICE_URL)
const updateProposalClient = new UpdateProposalClient(SERVICE_URL)
const updateProposalStatusClient = new UpdateProposalStatusClient(SERVICE_URL)
const archivingProposalClient = new ArchivingProposalClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  excel: null,
  proposal: null,
  detail: null,
  isProposalUpdate: false,
}

// Async Thunks
export const proposalGetAll = createAsyncThunk('proposal/getAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await searchProposalClient.search(
      params.name,
      params.proposalStatusId,
      params.projectId,
      params.companyId,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setProposal(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const proposalExcel = createAsyncThunk(
  'proposal/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchProposalClient.excel(
        params.name,
        params.proposalStatusId,
        params.projectId,
        params.companyId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const proposalGetId = createAsyncThunk('proposal/getId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getProposalClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const proposalDelete = createAsyncThunk('proposal/delete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await deleteProposalClient.delete(params)
    dispatch(setDeleted(true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const proposalArchiving = createAsyncThunk(
  'proposal/archiving',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await archivingProposalClient.archive(params)
      dispatch(setArchived(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const proposalCreate = createAsyncThunk('proposal/create', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await createProposalClient.create(params)
    dispatch(setRedirect(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const proposalUpdate = createAsyncThunk('proposal/update', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updateProposalClient.update(params)
    dispatch(setRedirect(response || params || true))
    dispatch(setDetail(params))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setDetail(params))
    dispatch(setError(error))
    throw error
  }
})

export const proposalUpdateStatus = createAsyncThunk(
  'proposal/updateStatus',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateProposalStatusClient.updateStatus(params)
      dispatch(setProposalUpdate(response || params || true))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const proposalSlice = createSlice({
  name: 'proposal',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setProposal: (state, action) => {
      state.proposal = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirectPage: (state, action) => {
      state.isRedirectPage = action.payload
      state.loading = false
    },
    setProposalUpdate: (state, action) => {
      state.isProposalUpdate = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setArchived: (state, action) => {
      state.isArchived = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('proposal/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setProposal,
  setDetail,
  setDeleted,
  setRedirectPage,
  setProposalUpdate,
  setRedirect,
  setArchived,
  setExcel,
  resetState,
} = proposalSlice.actions

// Selectors
export const selectProposalLoading = (state) => state.proposal.loading
export const selectProposalError = (state) => state.proposal.error
export const selectProposal = (state) => state.proposal.proposal
export const selectProposalDetail = (state) => state.proposal.detail
export const selectProposalDeleted = (state) => state.proposal.isDeleted
export const selectProposalRedirectPage = (state) => state.proposal.isRedirectPage
export const selectProposalUpdate = (state) => state.proposal.isProposalUpdate
export const selectProposalRedirect = (state) => state.proposal.isRedirect
export const selectProposalArchived = (state) => state.proposal.isArchived
export const selectProposalExcel = (state) => state.proposal.excel

export default proposalSlice.reducer
